import openai from '../lib/openai';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';
import { supabase } from '../lib/supabase';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Analyzes scout inquiry and generates AI-powered player recommendations.
 * @param {Object} inquiry - Scout inquiry object with requirements.
 * @returns {Promise<Object>} AI-generated player recommendations with match scores.
 */
export async function analyzeInquiryAndRecommendPlayers(inquiry) {
  try {
    // Fetch available players from database
    const { data: players, error: playersError } = await supabase
      ?.from('player_master_profiles')
      ?.select(`
        id,
        full_name,
        date_of_birth,
        nationality_primary,
        height,
        weight,
        preferred_foot,
        primary_position,
        secondary_positions,
        current_club,
        current_league,
        current_country,
        available_for_transfer,
        available_for_loan,
        current_market_value,
        currency,
        profile_photo,
        performance_stats!inner(
          season,
          appearances,
          goals_scored,
          assists,
          minutes_played,
          motm_awards
        )
      `)
      ?.eq('available_for_transfer', true)
      ?.limit(50);

    if (playersError) throw playersError;

    if (!players || players?.length === 0) {
      return { 
        recommendations: [], 
        analysis: {
          positionRequired: inquiry?.positionRequired || 'Not specified',
          budgetRange: `${inquiry?.budgetMin || 0} - ${inquiry?.budgetMax || 0} ${inquiry?.currency || 'EUR'}`,
          timeline: inquiry?.timelineDeadline || 'Not specified',
          specifications: inquiry?.playerSpecifications || {}
        },
        error: 'No available players found in database' 
      };
    }

    // Prepare player data for AI analysis
    const playerSummary = players?.map(p => {
      const latestStats = p?.performance_stats?.[0] || {};
      const age = p?.date_of_birth ? new Date()?.getFullYear() - new Date(p?.date_of_birth)?.getFullYear() : null;
      
      return {
        id: p?.id,
        name: p?.full_name,
        age,
        nationality: p?.nationality_primary,
        height: p?.height,
        weight: p?.weight,
        preferredFoot: p?.preferred_foot,
        position: p?.primary_position,
        secondaryPositions: p?.secondary_positions || [],
        currentClub: p?.current_club,
        league: p?.current_league,
        country: p?.current_country,
        marketValue: p?.current_market_value,
        currency: p?.currency,
        recentStats: {
          season: latestStats?.season,
          appearances: latestStats?.appearances || 0,
          goals: latestStats?.goals_scored || 0,
          assists: latestStats?.assists || 0,
          minutes: latestStats?.minutes_played || 0,
          motm: latestStats?.motm_awards || 0
        },
        availableForLoan: p?.available_for_loan
      };
    });

    // Prepare inquiry context for AI
    const inquiryContext = {
      title: inquiry?.title,
      description: inquiry?.description,
      positionRequired: inquiry?.positionRequired,
      budgetMin: inquiry?.budgetMin,
      budgetMax: inquiry?.budgetMax,
      currency: inquiry?.currency || 'EUR',
      deadline: inquiry?.timelineDeadline,
      specifications: inquiry?.playerSpecifications || {}
    };

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert football scout and talent analyst specializing in player-club matching. Analyze scout inquiries and recommend the best player matches based on position, budget, playing style, physical attributes, and performance metrics. Provide detailed reasoning for each recommendation, highlighting key strengths and fit factors.' 
        },
        { 
          role: 'user', 
          content: `Analyze this scout inquiry and recommend the top 5-8 best player matches from the available talent pool:\n\nSCOUT INQUIRY:\n${JSON.stringify(inquiryContext, null, 2)}\n\nAVAILABLE PLAYERS:\n${JSON.stringify(playerSummary, null, 2)}\n\nProvide recommendations ranked by compatibility score (0-100), with detailed match reasoning for each player.` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'player_recommendations',
          schema: {
            type: 'object',
            properties: {
              inquiryAnalysis: {
                type: 'object',
                properties: {
                  positionRequired: { type: 'string' },
                  budgetRange: { type: 'string' },
                  keyRequirements: { type: 'array', items: { type: 'string' } },
                  tacticalPreferences: { type: 'array', items: { type: 'string' } },
                  timeline: { type: 'string' }
                },
                required: ['positionRequired', 'budgetRange', 'keyRequirements']
              },
              recommendations: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    playerId: { type: 'string' },
                    playerName: { type: 'string' },
                    position: { type: 'string' },
                    age: { type: 'number' },
                    currentClub: { type: 'string' },
                    compatibilityScore: { type: 'number' },
                    matchReasoning: { type: 'string' },
                    keyStrengths: { type: 'array', items: { type: 'string' } },
                    technicalFit: { type: 'number' },
                    physicalFit: { type: 'number' },
                    experienceFit: { type: 'number' },
                    valueFit: { type: 'number' },
                    potentialConcerns: { type: 'array', items: { type: 'string' } },
                    transferFeasibility: { type: 'string' }
                  },
                  required: ['playerId', 'playerName', 'position', 'compatibilityScore', 'matchReasoning', 'keyStrengths']
                }
              }
            },
            required: ['inquiryAnalysis', 'recommendations'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'high'
    });

    const result = JSON.parse(response?.choices?.[0]?.message?.content);
    
    // Enrich recommendations with player photos
    const enrichedRecommendations = result?.recommendations?.map(rec => {
      const player = players?.find(p => p?.id === rec?.playerId);
      return {
        ...rec,
        profilePhoto: player?.profile_photo,
        marketValue: player?.current_market_value,
        currency: player?.currency
      };
    });

    return { 
      recommendations: enrichedRecommendations || [], 
      analysis: result?.inquiryAnalysis,
      error: null 
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in AI match recommendation:', error);
    }
    return { 
      recommendations: [], 
      analysis: null,
      error: errorInfo?.message 
    };
  }
}

/**
 * Fetches all transfer inquiries for the recommendation engine.
 * @returns {Promise<Object>} List of transfer inquiries.
 */
export async function fetchTransferInquiries() {
  try {
    const { data, error } = await supabase
      ?.from('transfer_inquiries')
      ?.select('*, club:user_profiles!transfer_inquiries_club_id_fkey(full_name, organization)')
      ?.eq('status', 'open')
      ?.order('created_at', { ascending: false })
      ?.limit(20);
    
    if (error) throw error;
    
    return { 
      inquiries: data?.map(inquiry => ({
        id: inquiry?.id,
        clubId: inquiry?.club_id,
        club: inquiry?.club,
        title: inquiry?.title,
        description: inquiry?.description,
        positionRequired: inquiry?.position_required,
        budgetMin: inquiry?.budget_min,
        budgetMax: inquiry?.budget_max,
        currency: inquiry?.currency,
        timelineDeadline: inquiry?.timeline_deadline,
        playerSpecifications: inquiry?.player_specifications,
        status: inquiry?.status,
        viewsCount: inquiry?.views_count,
        responsesCount: inquiry?.responses_count,
        createdAt: inquiry?.created_at
      })) || [],
      error: null 
    };
  } catch (error) {
    console.error('Error fetching transfer inquiries:', error);
    return { inquiries: [], error: error?.message };
  }
}